import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MixPanel from 'utils/mixpanelService';
import _get from 'lodash/get';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Divider from '@material-ui/core/Divider';
import useSiteCopySelector from 'components/useSiteCopySelector';
import withSizes from 'react-sizes';
import {
  EmailShareButton,
  FacebookShareButton,
  TwitterShareButton,
} from 'react-share';
import { getParentShareUrl } from 'utils/stringUtils';
import LinkIcon from '@material-ui/icons/Link';
import ButtonBase from '@material-ui/core/ButtonBase';
import ShareIcon from 'components/ShareWithDropdown/images/share.svg';
import FBIcon from 'components/ShareWithDropdown/images/facebook.svg';
import TwitterIcon from 'components/ShareWithDropdown/images/twitter.svg';
import EmailIcon from 'components/ShareWithDropdown/images/email.svg';

const useStyles = makeStyles(theme => ({
  buttonWrapper: {
    width: 115,
    border: '1px solid #E3E3E3',
    borderRadius: 5,
    height: 53,
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      border: 'none',
      width: 'auto',
      '& p': {
        display: 'none',
      },
      position: 'absolute',
      top: 8,
      right: 8,
    },
  },
  buttonRoot: {
    color: theme.palette.text.primary,
    padding: 0,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  shareIcon: {
    width: 15,
    height: 17,
    marginRight: 10,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  popperContainer: {
    zIndex: 999,
  },
  paper: {
    boxShadow: 'none',
    borderRadius: '3px !important',
    border: '1px solid #E3E3E3',
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  listItem: {
    '& > div': {
      display: 'flex',
    },
  },
  label: {
    fontSize: 12,
    lineHeight: 1.5,
    marginLeft: 8,
  },
  divider: {
    margin: '0 8%',
  },
  linkIcon: {
    fontSize: 18,
    marginRight: 5,
  },
  linkBtn: {
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily: 'MadaRegular',
    color: 'rgb(75, 75, 75)',
    '& input': {
      position: 'absolute',
      top: '-999999999px',
    },
  },
}));

const Share = ({ title, id }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [resourceItemPageSiteCopy] = useSiteCopySelector([
    'resource-item-page',
  ]);
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorEl = useRef();
  const linkRef = useRef();

  const handleToggle = () => setOpen(!open);

  const handleClose = event => {
    if (anchorEl.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleClick = social => {
    MixPanel.track(`Sharing Resource`, {
      name: title,
      type: 'assessment',
      social,
      entryId: id,
    });
  };

  const handleCopyLink = () => {
    linkRef.current.select();
    document.execCommand('copy');
  };

  const parentHref = getParentShareUrl();
  const shareUrl = parentHref || window.location.href;
  const shareQuote = `${title} @CredibleMind`;

  return (
    <>
      <div className={classes.buttonWrapper}>
        <ButtonBase
          buttonRef={anchorEl}
          aria-owns={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          className={classes.buttonRoot}
          disableRipple
          arial-label="Share"
        >
          <img src={ShareIcon} className={classes.shareIcon} alt="Share" />
          <Typography variant="body2">
            {_get(resourceItemPageSiteCopy, 'pageCopy.shareResourceLabel')}
          </Typography>
        </ButtonBase>
      </div>
      <Popper
        open={open}
        anchorEl={anchorEl.current}
        className={classes.popperContainer}
        transition
        placement={isSmDown ? 'bottom-end' : 'bottom'}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            id="menu-list-grow"
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper className={classes.paper}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList className={classes.list}>
                  <MenuItem className={classes.listItem}>
                    <FacebookShareButton
                      url={shareUrl}
                      quote={shareQuote}
                      beforeOnClick={() => handleClick('facebook')}
                    >
                      <img src={FBIcon} alt="facebook" />
                      <Typography className={classes.label}>
                        {_get(
                          resourceItemPageSiteCopy,
                          'pageCopy.shareFacebookLabel',
                        )}
                      </Typography>
                    </FacebookShareButton>
                  </MenuItem>
                  <Divider className={classes.divider} />
                  <MenuItem className={classes.listItem}>
                    <TwitterShareButton
                      url={shareUrl}
                      title={shareQuote}
                      beforeOnClick={() => handleClick('twitter')}
                    >
                      <img src={TwitterIcon} alt="twitter" />
                      <Typography className={classes.label}>
                        {_get(
                          resourceItemPageSiteCopy,
                          'pageCopy.shareTwitterLabel',
                        )}
                      </Typography>
                    </TwitterShareButton>
                  </MenuItem>
                  <Divider className={classes.divider} />
                  <MenuItem className={classes.listItem}>
                    <EmailShareButton
                      url={shareUrl}
                      subject={shareQuote}
                      beforeOnClick={() => handleClick('email')}
                    >
                      <img src={EmailIcon} alt="email" />
                      <Typography className={classes.label}>
                        {_get(
                          resourceItemPageSiteCopy,
                          'pageCopy.shareEmailLabel',
                        )}
                      </Typography>
                    </EmailShareButton>
                  </MenuItem>
                  <Divider className={classes.divider} />
                  <MenuItem className={classes.listItem}>
                    <ButtonBase
                      className={classes.linkBtn}
                      onClick={handleCopyLink}
                      disableRipple
                    >
                      <input
                        type="text"
                        ref={link => {
                          linkRef.current = link;
                        }}
                        value={shareUrl}
                      />
                      <LinkIcon
                        className={classes.linkIcon}
                        color="textPrimary"
                      />
                      <Typography className={classes.label}>
                        {_get(
                          resourceItemPageSiteCopy,
                          'pageCopy.shareLinkLabel',
                        )}
                      </Typography>
                    </ButtonBase>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

Share.propTypes = {
  title: PropTypes.string,
};

const mapSizesToProps = ({ width }) => ({
  mobileShare: width < 750,
});

export default withSizes(mapSizesToProps)(Share);
